import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { 
  CurrencyDollarIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const TierForm = () => {
  const navigate = useNavigate();
  const { itemId, id } = useParams();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    tier_name: '',
    min_quantity: '',
    max_quantity: '',
    base_price: '',
    per_unit_price: '',
    setup_fee: '',
    tier_price: '',
    billing_frequency_id: '',
    sort_order: 1,
    is_active: true
  });

  const [item, setItem] = useState(null);
  const [frequencies, setFrequencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchDropdownData();
    fetchItem();
    if (isEditing) {
      fetchTier();
    }
  }, [itemId, id]);

  const fetchDropdownData = async () => {
    try {
      const response = await api.get('/price-book/frequencies');
      setFrequencies(response.data.filter(freq => freq.is_active));
    } catch (err) {
      console.error('Error fetching frequencies:', err);
    }
  };

  const fetchItem = async () => {
    try {
      const response = await api.get(`/price-book/items/${itemId}`);
      setItem(response.data);
    } catch (err) {
      console.error('Error fetching item:', err);
      setError('Failed to load item data');
    }
  };

  const fetchTier = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/price-book/tiers/${id}`);
      const tier = response.data;
      
      setFormData({
        tier_name: tier.tier_name || '',
        min_quantity: tier.min_quantity || '',
        max_quantity: tier.max_quantity || '',
        base_price: tier.base_price || '',
        per_unit_price: tier.per_unit_price || '',
        setup_fee: tier.setup_fee || '',
        tier_price: tier.tier_price || '',
        billing_frequency_id: tier.billing_frequency_id || '',
        sort_order: tier.sort_order || 1,
        is_active: tier.is_active !== false
      });
    } catch (err) {
      console.error('Error fetching tier:', err);
      setError('Failed to load tier data');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.tier_name.trim()) {
      errors.tier_name = 'Tier name is required';
    }

    if (!formData.min_quantity || formData.min_quantity < 1) {
      errors.min_quantity = 'Minimum quantity must be at least 1';
    }

    if (formData.max_quantity && parseInt(formData.max_quantity) <= parseInt(formData.min_quantity)) {
      errors.max_quantity = 'Maximum quantity must be greater than minimum quantity';
    }

    // Either base_price OR tier_price must be provided
    const hasBasePrice = formData.base_price && parseFloat(formData.base_price) >= 0;
    const hasTierPrice = formData.tier_price && parseFloat(formData.tier_price) >= 0;
    
    if (!hasBasePrice && !hasTierPrice) {
      errors.base_price = 'Either Base Price or Tier Price is required';
      errors.tier_price = 'Either Base Price or Tier Price is required';
    }

    if (formData.base_price && parseFloat(formData.base_price) < 0) {
      errors.base_price = 'Base price cannot be negative';
    }

    if (formData.per_unit_price && parseFloat(formData.per_unit_price) < 0) {
      errors.per_unit_price = 'Per unit price cannot be negative';
    }

    if (formData.setup_fee && parseFloat(formData.setup_fee) < 0) {
      errors.setup_fee = 'Setup fee cannot be negative';
    }

    if (formData.tier_price && parseFloat(formData.tier_price) < 0) {
      errors.tier_price = 'Tier price cannot be negative';
    }

    if (!formData.billing_frequency_id) {
      errors.billing_frequency_id = 'Billing frequency is required';
    }

    if (formData.sort_order < 1) {
      errors.sort_order = 'Sort order must be at least 1';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = {
        price_book_item_id: parseInt(itemId),
        tier_name: formData.tier_name,
        min_quantity: parseInt(formData.min_quantity),
        max_quantity: formData.max_quantity ? parseInt(formData.max_quantity) : null,
        base_price: parseFloat(formData.base_price),
        per_unit_price: formData.per_unit_price ? parseFloat(formData.per_unit_price) : null,
        setup_fee: formData.setup_fee ? parseFloat(formData.setup_fee) : 0,
        tier_price: formData.tier_price ? parseFloat(formData.tier_price) : null,
        billing_frequency_id: parseInt(formData.billing_frequency_id),
        sort_order: parseInt(formData.sort_order),
        is_active: formData.is_active
      };

      if (isEditing) {
        await api.put(`/price-book/tiers/${id}`, submitData);
      } else {
        await api.post('/price-book/tiers', submitData);
      }

      navigate(`/price-book/items/${itemId}/tiers`);
    } catch (err) {
      console.error('Error saving tier:', err);
      setError(err.response?.data?.error || 'Failed to save tier');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return parseFloat(price).toFixed(2);
  };

  if (loading && isEditing) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center">
          <button
            onClick={() => navigate(`/price-book/items/${itemId}/tiers`)}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEditing ? 'Edit Price Tier' : 'Add New Price Tier'}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {item && (
                <>
                  For item: <span className="font-medium">{item.item_name}</span>
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Tier Name */}
            <div className="sm:col-span-2">
              <label htmlFor="tier_name" className="block text-sm font-medium text-gray-700">
                Tier Name *
              </label>
              <input
                type="text"
                name="tier_name"
                id="tier_name"
                value={formData.tier_name}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.tier_name ? 'border-red-300' : ''
                }`}
                placeholder="e.g., Standard, Volume Discount, Enterprise"
              />
              {validationErrors.tier_name && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.tier_name}</p>
              )}
            </div>

            {/* Quantity Range */}
            <div>
              <label htmlFor="min_quantity" className="block text-sm font-medium text-gray-700">
                Minimum Quantity *
              </label>
              <input
                type="number"
                name="min_quantity"
                id="min_quantity"
                min="1"
                value={formData.min_quantity}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.min_quantity ? 'border-red-300' : ''
                }`}
                placeholder="1"
              />
              {validationErrors.min_quantity && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.min_quantity}</p>
              )}
            </div>

            <div>
              <label htmlFor="max_quantity" className="block text-sm font-medium text-gray-700">
                Maximum Quantity
              </label>
              <input
                type="number"
                name="max_quantity"
                id="max_quantity"
                min="1"
                value={formData.max_quantity}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.max_quantity ? 'border-red-300' : ''
                }`}
                placeholder="Leave blank for unlimited"
              />
              {validationErrors.max_quantity && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.max_quantity}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Leave blank for unlimited quantity
              </p>
            </div>

            {/* Pricing */}
            <div>
              <label htmlFor="base_price" className="block text-sm font-medium text-gray-700">
                Base Price {formData.tier_price ? '' : '*'}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  name="base_price"
                  id="base_price"
                  min="0"
                  step="0.01"
                  value={formData.base_price}
                  onChange={handleChange}
                  className={`pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                    validationErrors.base_price ? 'border-red-300' : ''
                  }`}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.base_price && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.base_price}</p>
              )}
            </div>

            <div>
              <label htmlFor="per_unit_price" className="block text-sm font-medium text-gray-700">
                Per Unit Price
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  name="per_unit_price"
                  id="per_unit_price"
                  min="0"
                  step="0.01"
                  value={formData.per_unit_price}
                  onChange={handleChange}
                  className={`pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                    validationErrors.per_unit_price ? 'border-red-300' : ''
                  }`}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.per_unit_price && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.per_unit_price}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Optional additional charge per unit (added to base price)
              </p>
            </div>

            <div>
              <label htmlFor="setup_fee" className="block text-sm font-medium text-gray-700">
                Setup Fee
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  name="setup_fee"
                  id="setup_fee"
                  min="0"
                  step="0.01"
                  value={formData.setup_fee}
                  onChange={handleChange}
                  className={`pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                    validationErrors.setup_fee ? 'border-red-300' : ''
                  }`}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.setup_fee && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.setup_fee}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Optional one-time setup fee
              </p>
            </div>

            <div>
              <label htmlFor="tier_price" className="block text-sm font-medium text-gray-700">
                Tier Price
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  name="tier_price"
                  id="tier_price"
                  min="0"
                  step="0.01"
                  value={formData.tier_price}
                  onChange={handleChange}
                  className={`pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                    validationErrors.tier_price ? 'border-red-300' : ''
                  }`}
                  placeholder="0.00"
                />
              </div>
              {validationErrors.tier_price && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.tier_price}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Optional fixed price for this tier (when set, overrides base + per-unit calculation)
              </p>
            </div>

            {/* Billing Frequency */}
            <div>
              <label htmlFor="billing_frequency_id" className="block text-sm font-medium text-gray-700">
                Billing Frequency *
              </label>
              <select
                name="billing_frequency_id"
                id="billing_frequency_id"
                value={formData.billing_frequency_id}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.billing_frequency_id ? 'border-red-300' : ''
                }`}
              >
                <option value="">Select Frequency</option>
                {frequencies.map(frequency => (
                  <option key={frequency.id} value={frequency.id}>
                    {frequency.display_label}
                  </option>
                ))}
              </select>
              {validationErrors.billing_frequency_id && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.billing_frequency_id}</p>
              )}
            </div>

            {/* Sort Order */}
            <div>
              <label htmlFor="sort_order" className="block text-sm font-medium text-gray-700">
                Sort Order
              </label>
              <input
                type="number"
                name="sort_order"
                id="sort_order"
                min="1"
                value={formData.sort_order}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.sort_order ? 'border-red-300' : ''
                }`}
              />
              {validationErrors.sort_order && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.sort_order}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Controls the display order (lower numbers appear first)
              </p>
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center">
            <input
              id="is_active"
              name="is_active"
              type="checkbox"
              checked={formData.is_active}
              onChange={handleChange}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
              Active
            </label>
            <p className="ml-2 text-xs text-gray-500">
              Inactive tiers are hidden from selection lists
            </p>
          </div>

          {/* Pricing Summary */}
          {(formData.base_price || formData.tier_price) && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Pricing Summary</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <div>
                  Quantity Range: {formData.min_quantity} - {formData.max_quantity || 'Unlimited'}
                </div>
                
                {formData.tier_price ? (
                  // Show tier price model
                  <div>
                    <div className="font-medium text-indigo-600">
                      Fixed Tier Price: ${formatPrice(formData.tier_price)}
                    </div>
                    {formData.setup_fee && (
                      <div>Setup Fee: ${formatPrice(formData.setup_fee)}</div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                      Total cost for any quantity in this tier: ${formatPrice(parseFloat(formData.tier_price || 0) + parseFloat(formData.setup_fee || 0))}
                    </div>
                  </div>
                ) : (
                  // Show base + per-unit model
                  <div>
                    <div>
                      Base Price: ${formatPrice(formData.base_price)}
                      {formData.per_unit_price && ` + $${formatPrice(formData.per_unit_price)} per unit`}
                      {formData.setup_fee && ` + $${formatPrice(formData.setup_fee)} setup fee`}
                    </div>
                    {formData.per_unit_price && (
                      <div className="text-xs text-gray-500">
                        Example: For {formData.min_quantity} units = ${formatPrice(formData.base_price)} + (${formatPrice(formData.per_unit_price)} × {formData.min_quantity}) = ${formatPrice(parseFloat(formData.base_price || 0) + (parseFloat(formData.per_unit_price || 0) * parseInt(formData.min_quantity || 1)))}
                      </div>
                    )}
                  </div>
                )}
                
                {frequencies.find(f => f.id === parseInt(formData.billing_frequency_id)) && (
                  <div>
                    Billing: {frequencies.find(f => f.id === parseInt(formData.billing_frequency_id)).display_label}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(`/price-book/items/${itemId}/tiers`)}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update Tier' : 'Create Tier'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TierForm; 